import { supabase } from '../lib/supabase';
import { googleDriveService } from './googleDriveService';

export const mediaService = {
  // Upload media file to Google Drive and save metadata to Supabase
  async uploadMedia(file, galleryId, folderId, metadata = {}) {
    try {
      // Upload to Google Drive
      const driveFile = await googleDriveService?.uploadFile(file, folderId, metadata);
      
      // Make file publicly accessible
      const publicFile = await googleDriveService?.makeFilePublic(driveFile?.id);
      
      // Determine media type
      const mediaType = file?.type?.startsWith('image/') ? 'photo' : 'video';
      
      // Save metadata to Supabase
      const { data, error } = await supabase?.from('media')?.insert({
        gallery_id: galleryId,
        file_name: file?.name,
        file_size: file?.size,
        media_type: mediaType,
        file_url: googleDriveService?.getEmbedUrl(driveFile?.id),
        thumbnail_url: driveFile?.thumbnailLink || googleDriveService?.getThumbnailUrl(driveFile?.id),
        drive_file_id: driveFile?.id,
        drive_thumbnail_link: driveFile?.thumbnailLink,
        drive_web_view_link: publicFile?.webViewLink,
        title: metadata?.title || file?.name,
        description: metadata?.description || '',
        alt_text: metadata?.altText || `${mediaType} file: ${file?.name}`,
        download_enabled: metadata?.downloadEnabled ?? true
      })?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  },

  // Sync media from Google Drive folder to Supabase
  async syncFolderMedia(galleryId, folderId, folderType = 'compressed') {
    try {
      // Get media files from Google Drive
      const driveMedia = await googleDriveService?.syncFolder(folderId);
      
      // Get existing media for this gallery
      const { data: existingMedia } = await supabase?.from('media')?.select('drive_file_id')?.eq('gallery_id', galleryId);
      
      const existingFileIds = new Set(existingMedia?.map(m => m.drive_file_id) || []);
      
      // Filter out already synced files
      const newMedia = driveMedia?.filter(m => !existingFileIds?.has(m?.driveFileId));
      
      // Insert new media records
      if (newMedia?.length > 0) {
        const mediaRecords = newMedia?.map(m => ({
          gallery_id: galleryId,
          file_name: m?.fileName,
          file_size: m?.fileSize,
          media_type: m?.mediaType,
          file_url: m?.fileUrl,
          thumbnail_url: m?.thumbnailUrl,
          drive_file_id: m?.driveFileId,
          drive_thumbnail_link: m?.thumbnailUrl,
          drive_web_view_link: m?.webViewLink,
          alt_text: `${m?.mediaType} file: ${m?.fileName}`,
          download_enabled: true
        }));
        
        const { data, error } = await supabase?.from('media')?.insert(mediaRecords)?.select();
        
        if (error) throw error;
        
        // Update gallery last_sync_at
        await supabase?.from('galleries')?.update({ last_sync_at: new Date()?.toISOString() })?.eq('id', galleryId);
        
        return { synced: data?.length, total: driveMedia?.length };
      }
      
      return { synced: 0, total: driveMedia?.length };
    } catch (error) {
      console.error('Error syncing folder media:', error);
      throw error;
    }
  },

  // Get all media for a gallery
  async getGalleryMedia(galleryId, filters = {}) {
    try {
      let query = supabase?.from('media')?.select('*')?.eq('gallery_id', galleryId);
      
      // Apply filters
      if (filters?.mediaType && filters?.mediaType !== 'all') {
        query = query?.eq('media_type', filters?.mediaType);
      }
      
      // Apply sorting
      const sortField = filters?.sortBy?.includes('date') ? 'created_at' : 'file_name';
      const sortOrder = filters?.sortBy?.includes('desc') ? { ascending: false } : { ascending: true };
      query = query?.order(sortField, sortOrder);
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching gallery media:', error);
      throw error;
    }
  },

  // Update media metadata
  async updateMedia(mediaId, updates) {
    try {
      const { data, error } = await supabase?.from('media')?.update({
          title: updates?.title,
          description: updates?.description,
          alt_text: updates?.altText,
          download_enabled: updates?.downloadEnabled
        })?.eq('id', mediaId)?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating media:', error);
      throw error;
    }
  },

  // Delete media (from both Supabase and Google Drive)
  async deleteMedia(mediaId) {
    try {
      // Get media record to get drive_file_id
      const { data: media, error: fetchError } = await supabase?.from('media')?.select('drive_file_id')?.eq('id', mediaId)?.single();
      
      if (fetchError) throw fetchError;
      
      // Delete from Google Drive if drive_file_id exists
      if (media?.drive_file_id) {
        try {
          await googleDriveService?.deleteFile(media?.drive_file_id);
        } catch (driveError) {
          console.warn('Failed to delete from Google Drive:', driveError);
          // Continue with Supabase deletion even if Drive deletion fails
        }
      }
      
      // Delete from Supabase
      const { error: deleteError } = await supabase?.from('media')?.delete()?.eq('id', mediaId);
      
      if (deleteError) throw deleteError;
      return true;
    } catch (error) {
      console.error('Error deleting media:', error);
      throw error;
    }
  },

  // Bulk delete media
  async bulkDeleteMedia(mediaIds) {
    try {
      const results = await Promise.allSettled(
        mediaIds?.map(id => this.deleteMedia(id))
      );
      
      const successful = results?.filter(r => r?.status === 'fulfilled')?.length;
      const failed = results?.filter(r => r?.status === 'rejected')?.length;
      
      return { successful, failed, total: mediaIds?.length };
    } catch (error) {
      console.error('Error bulk deleting media:', error);
      throw error;
    }
  },

  // Toggle download permission for media
  async toggleDownload(mediaId, enabled) {
    try {
      const { data, error } = await supabase?.from('media')?.update({ download_enabled: enabled })?.eq('id', mediaId)?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling download:', error);
      throw error;
    }
  },

  // Increment view count
  async incrementViewCount(mediaId) {
    try {
      const { error } = await supabase?.rpc('increment_view_count', {
        media_id: mediaId
      });
      
      if (error) {
        // Fallback if RPC doesn't exist
        const { data: media } = await supabase?.from('media')?.select('view_count')?.eq('id', mediaId)?.single();
        
        await supabase?.from('media')?.update({ view_count: (media?.view_count || 0) + 1 })?.eq('id', mediaId);
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  },

  // Increment download count
  async incrementDownloadCount(mediaId) {
    try {
      const { error } = await supabase?.rpc('increment_download_count', {
        media_id: mediaId
      });
      
      if (error) {
        // Fallback if RPC doesn't exist
        const { data: media } = await supabase?.from('media')?.select('download_count')?.eq('id', mediaId)?.single();
        
        await supabase?.from('media')?.update({ download_count: (media?.download_count || 0) + 1 })?.eq('id', mediaId);
      }
    } catch (error) {
      console.error('Error incrementing download count:', error);
    }
  }
};